import * as React from "react";
import * as React44ms from "../../../Util/form-utils/react-44ms";

import {
  AvailableShippingMethod,
  BillingAddressInput,
  BillingCartAddress,
  CartAddressInput,
  CheckoutRegions_Response,
  ACountry,
  CustomerAddressInput,
  CustomerInformation_Response,
  LegacyMagento_ShippingInformation_CartAddressInput,
  MagentoCountryCodeEnum,
  ShippingCartAddress,
  ShippingMethodInput,
  useCustomerCartId,
  useCustomerInformationQuery,
  useCustomerSession,
  useDataSourceCheckoutRegionsQuery,
  useProminentError,
} from "../../../Common";
import { ReactCheckout2022_AllTheForms } from "../../all-the-forms";
import {
  LegacyCheckoutConfig,
  readLegacyCheckoutConfig,
} from "../../../Common/legacy-checkout-config/read-legacy-checkout-config";
import { requireAsPromise } from "../../../Common/legacy-checkout-config/require-as-promise";
import { getLastKnownAddress } from "../../delivery-details/address-sync-cache";
import {
  assertCartItemsAvailable,
  KickoffDeps,
  MISSING_CHECKOUT_CONFIG_MESSAGE,
  runPaymentPageKickoff,
} from "./kickoff-orchestration";
import {
  BillingAddressFormExpects,
  DeliveryDetailsForm,
  GenericAddressFormExpects,
  ShippingAddressFormExpects,
} from "../../model";
import { useDataMutableSourceShippingClientStorageData_asController } from "../data-mutable-source-shipping-client-storage-data-provider";

import {
  DataMutationCreateCustomerAddress_Request,
  useDataMutationCreateCustomerAddress_asController,
} from "../data-mutation--create-customer-address";
import {
  DataMutationShippingMemberSetAuthorityToLeave_Request,
  useDataMutationShippingMemberSetAuthorityToLeave_asController,
} from "../data-mutation--shipping-member-set-authority-to-leave";
import {
  DataMutationShippingSetAuthorityToLeave_Request,
  useDataMutationShippingSetAuthorityToLeave_asController,
} from "../data-mutation--shipping-set-authority-to-leave";
import {
  DataMutationShippingSetBillingAddressesOnCart_Request,
  useDataMutationShippingSetBillingAddressesOnCart_asController,
} from "../data-mutation--shipping-set-billing-addresses-on-cart";
import {
  DataMutationShippingSetGuestEmail_Request,
  useDataMutationShippingSetGuestEmail_asController,
} from "../data-mutation--shipping-set-guest-email";
import {
  DataMutationShippingSetShippingAddressesOnCart_Request,
  useDataMutationShippingSetShippingAddressesOnCart_asController,
} from "../data-mutation--shipping-set-shipping-addresses-on-cart";
import {
  DataMutationShippingSetShippingMethodsOnCart_Request,
  useDataMutationShippingSetShippingMethodsOnCart_asController,
} from "../data-mutation--shipping-set-shipping-methods-on-cart";
import {
  DataSourceLegacyCheckoutConfig_Request,
  useDataSourceLegacyCheckoutConfig_asController,
} from "../data-source--legacy-checkout-config";
import { useDataSourceMyBagTotalsController } from "../data-source--mybag-totals-provider";
import { useMyBagAnalytics } from "../my-bag-analytics-provider";
import { useMyBagPresentationModel } from "../my-bag-provider";
import {
  ReactCheckout2022Route,
  useReactCheckout2022Router_asController,
} from "../react-checkout-2022-router-provider";

export interface DeliveryDetailsPaymentPageKickoffConsumerInterface {
  isLoading: boolean;
  hasBeenToThePaymentPage: boolean;
}
interface LegacyMagentoCustomer {
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  completelySetTheCustomerData: (customerData: unknown) => void;
}

const defaultValue =
  {} as unknown as DeliveryDetailsPaymentPageKickoffConsumerInterface;
const DeliveryDetailsPaymentPageKickoffContext =
  React.createContext(defaultValue);

export interface DeliveryDetailsPaymentPageKickoff_ControllerInterface {
  kickoffThePaymentPage: () => Promise<void>;
  primeDeliveryDetailsSnapshot: (
    deliveryDetailsFormState: DeliveryDetailsForm | null
  ) => void;
}
const controllerDefaultValue =
  {} as unknown as DeliveryDetailsPaymentPageKickoff_ControllerInterface;
const DeliveryDetailsPaymentPageKickoff_ControllerContext = React.createContext(
  controllerDefaultValue
);

export const DeliveryDetailsPaymentPageKickoffProvider: React.FunctionComponent =
  (props) => {
    const { setProminentError } = useProminentError();
    const { navigateTo } = useReactCheckout2022Router_asController();

    const { mutateAsync: dataMutation_shippingSetGuestEmail_mutateAsync } =
      useDataMutationShippingSetGuestEmail_asController();
    const { cartId } = useCustomerCartId();
    const { isCustomerLoggedIn } = useCustomerSession();
    const { data: customerInformationData } = useCustomerInformationQuery();
    const { analytics } = useMyBagAnalytics();
    const { mutateAsync: dataMutation_createCustomerAddress_mutateAsync } =
      useDataMutationCreateCustomerAddress_asController();
    const { data: dataSource_checkoutRegions_data } =
      useDataSourceCheckoutRegionsQuery();
    const { handleLastSelectedSavedAddressIdChange } =
      useDataMutableSourceShippingClientStorageData_asController();
    const { mutateAsync: dataMutation_setShippingAddressesOnCart_mutateAsync } =
      useDataMutationShippingSetShippingAddressesOnCart_asController();
    const { mutateAsync: dataMutation_setShippingMethodsOnCart_mutateAsync } =
      useDataMutationShippingSetShippingMethodsOnCart_asController();
    const { mutateAsync: dataMutation_setAuthorityToLeave_mutateAsync } =
      useDataMutationShippingSetAuthorityToLeave_asController();
    const { mutateAsync: dataMutation_member_setAuthorityToLeave_mutateAsync } =
      useDataMutationShippingMemberSetAuthorityToLeave_asController();
    const { mutateAsync: dataMutation_setBillingAddressesOnCart_mutateAsync } =
      useDataMutationShippingSetBillingAddressesOnCart_asController();
    const {
      enablePoolingInvalidations:
        dataSourceMyBagTotals_enablePoolingInvalidations,
      flushPooledInvalidations: dataSourceMyBagTotals_flushPooledInvalidations,
    } = useDataSourceMyBagTotalsController();

    const { mutateAsync: fetchLegacyCheckoutConfig } =
      useDataSourceLegacyCheckoutConfig_asController();

    const { presentationMyBagOrder } = useMyBagPresentationModel();

    const getCurrentFormState = React44ms.React.useFormRef();
    const deliveryDetailsFormStateSnapshotRef =
      React.useRef<DeliveryDetailsForm | null>(null);

    const [isLoading, setIsLoading] = React.useState(false);
    const [hasBeenToThePaymentPage, setHasBeenToThePaymentPage] =
      React.useState(false);

    const getDeliveryDetailsFormState = React.useCallback(() => {
      const snapshot = deliveryDetailsFormStateSnapshotRef.current;
      if (snapshot) {
        return snapshot;
      }
      const formState = getCurrentFormState();
      const deliveryDetailsFormState = (
        formState as ReactCheckout2022_AllTheForms | null
      )?.deliveryDetails;
      return deliveryDetailsFormState;
    }, [getCurrentFormState]);

    const getDeliveryEmailValue = React.useCallback(() => {
      const deliveryDetailsFormState = getDeliveryDetailsFormState();
      const emailFromForm =
        deliveryDetailsFormState?.shippingEmailConfirmation.value;
      return emailFromForm || null;
    }, [getDeliveryDetailsFormState]);

    const primeDeliveryDetailsSnapshot: DeliveryDetailsPaymentPageKickoff_ControllerInterface["primeDeliveryDetailsSnapshot"] =
      React.useCallback((deliveryDetailsFormState) => {
        deliveryDetailsFormStateSnapshotRef.current = deliveryDetailsFormState;
      }, []);

    const doesTheAddressNeedToBeSaved_shipping = React.useCallback(() => {
      const deliveryDetailsFormState = getDeliveryDetailsFormState();

      let saveInCustomerAddressBook;
      if (!isCustomerLoggedIn) {
        saveInCustomerAddressBook = false;
      } else if (
        isCustomerLoggedIn &&
        (customerInformationData?.addresses?.length || 0) === 0
      ) {
        saveInCustomerAddressBook = true;
      } else if (
        isCustomerLoggedIn &&
        !deliveryDetailsFormState?.shipping.savedAddress.enabled
      ) {
        saveInCustomerAddressBook = true;
      } else {
        saveInCustomerAddressBook = false;
      }

      return saveInCustomerAddressBook;
    }, [
      isCustomerLoggedIn,
      customerInformationData,
      customerInformationData?.addresses,
    ]);
    const doesTheAddressNeedToBeSaved_billing = React.useCallback(() => {
      return false;
    }, []);

    /**
     * Sign: Internal handler components
     */
    const handle_magentoLegacy_checkoutConfig_updates =
      React.useCallback(async () => {
        const variables: DataSourceLegacyCheckoutConfig_Request = {};
        const newCheckoutConfig = await fetchLegacyCheckoutConfig(variables);

        const legacyWindow = window as Window & {
          customerData?: unknown;
        };
        await requireAsPromise(
          ["jquery", "Magento_Customer/js/model/customer"],
          (_jquery, loadedCustomer) => {
            const customer = loadedCustomer as LegacyMagentoCustomer;
            customer.setIsLoggedIn(isCustomerLoggedIn);
            legacyWindow.customerData = newCheckoutConfig.data.customerData;
            customer.completelySetTheCustomerData(legacyWindow.customerData);
          }
        );

        const emailFromForm = getDeliveryEmailValue();
        const checkoutConfig: (LegacyCheckoutConfig & {
          validatedEmailValue?: string | null;
        }) | undefined = readLegacyCheckoutConfig();
        if (!checkoutConfig) {
          throw new Error(MISSING_CHECKOUT_CONFIG_MESSAGE);
        }
        checkoutConfig.validatedEmailValue =
          checkoutConfig.validatedEmailValue || emailFromForm;
      }, [isCustomerLoggedIn, fetchLegacyCheckoutConfig, getDeliveryEmailValue]);
    const handle_cart_guestCart_setTheEmail = React.useCallback(async () => {
      if (isCustomerLoggedIn) {
        return;
      }

      /**
       * Neither of these guards not cartId or email should be necessary at all.
       */
      if (!cartId) {
        throw new Error(
          "Cannot set the guest email on the cart because the cart ID is not available."
        );
      }

      const email = getDeliveryEmailValue();
      if (!email) {
        throw new Error(
          "Cannot set the guest email on the cart because the email is not available."
        );
      }

      const request: DataMutationShippingSetGuestEmail_Request = {
        cartId,
        email,
      };
      const dataMutation_shippingSetGuestEmail_response =
        await dataMutation_shippingSetGuestEmail_mutateAsync(request);
      assertCartItemsAvailable(dataMutation_shippingSetGuestEmail_response);

      if (dataMutation_shippingSetGuestEmail_response.email !== email) {
        throw new Error("Unable to set the email address on the cart.");
      }

      // Triple Whale Contact: the guest's e-mail is now on the cart; pending funnel
      // steps (checkoutStarted) replay from here.
      analytics.contactCaptured({ email });
    }, [
      isCustomerLoggedIn,
      cartId,
      dataMutation_shippingSetGuestEmail_mutateAsync,
      getDeliveryEmailValue,
      analytics,
    ]);

    const handle_generic_saveTheAddress = React.useCallback(
      async (genericAddressFormState: GenericAddressFormExpects) => {
        const address: CustomerAddressInput =
          transform_shippingForm_toCustomerAddressInput(
            dataSource_checkoutRegions_data,
            genericAddressFormState
          );
        const request: DataMutationCreateCustomerAddress_Request = {
          address,
        };
        const dataMutation_createCustomerAddress_response =
          await dataMutation_createCustomerAddress_mutateAsync(request);
      },
      [
        dataSource_checkoutRegions_data,
        dataMutation_createCustomerAddress_mutateAsync,
      ]
    );

    const handle_shippingAddress__saveTheAddress =
      React.useCallback(async () => {
        const deliveryDetailsFormState = getDeliveryDetailsFormState();
        const shippingAddressFormState = deliveryDetailsFormState?.shipping;
        const doesTheAddressNeedToBeSaved =
          doesTheAddressNeedToBeSaved_shipping();
        if (shippingAddressFormState && doesTheAddressNeedToBeSaved) {
          await handle_generic_saveTheAddress(
            deliveryDetailsFormState?.shipping
          );
        }

        if (shippingAddressFormState?.savedAddress.enabled) {
          const addressId = shippingAddressFormState.savedAddress.value;
          await handleLastSelectedSavedAddressIdChange(addressId);
        }
      }, [
        doesTheAddressNeedToBeSaved_shipping,
        handle_generic_saveTheAddress,
        handleLastSelectedSavedAddressIdChange,
      ]);
    const handle_shippingAddress__setTheAddress =
      React.useCallback(async (): Promise<
        [ShippingCartAddress, AvailableShippingMethod]
      > => {
        if (!cartId) {
          throw new Error(
            "Cannot set the shipping address on the cart because the cart ID is not available."
          );
        }

        const deliveryDetailsFormState = getDeliveryDetailsFormState();
        const shippingAddressFormState = deliveryDetailsFormState?.shipping;
        if (!shippingAddressFormState) {
          throw new Error(
            "Cannot set the shipping address on the cart because the shipping address form state is not available."
          );
        }

        const address: CartAddressInput | null =
          transform_genericAddressForm_toCartAddressInput(
            dataSource_checkoutRegions_data,
            customerInformationData,
            shippingAddressFormState
          );
        if (!address) {
          throw new Error(
            "Cannot set the shipping address on the cart because the address is invalid without a Country."
          );
        }
        const request: DataMutationShippingSetShippingAddressesOnCart_Request =
          {
            cart_id: cartId,
            shipping_addresses: [
              {
                address,
              },
            ],
          };
        const dataMutation_setShippingAddressesOnCart_response =
          await dataMutation_setShippingAddressesOnCart_mutateAsync(request);
        assertCartItemsAvailable(dataMutation_setShippingAddressesOnCart_response);

        let thisShippingAddress: ShippingCartAddress | null = null;
        let thisShippingMethod: AvailableShippingMethod | null = null;
        const shippingAddresses =
          dataMutation_setShippingAddressesOnCart_response.shipping_addresses ||
          [];
        if (shippingAddresses.length > 0) {
          const firstShippingAddress = shippingAddresses[0];
          if (firstShippingAddress) {
            thisShippingAddress = firstShippingAddress;

            const availableShippingMethods =
              firstShippingAddress.available_shipping_methods || [];
            if (availableShippingMethods.length > 0) {
              // The freeshipping rate is only offered when the backend decided
              // the cart qualifies (BBNT AddressPlugin/Freeshipping carrier), so
              // prefer it over whatever carrier sort order puts first — setting
              // [0] blindly could persist flatrate's $15 on a free-shipping cart
              // and the mutation response would feed that phantom to Review & Pay.
              const preferredShippingMethod =
                availableShippingMethods.find(
                  (shippingMethod) =>
                    shippingMethod?.carrier_code === "freeshipping"
                ) || availableShippingMethods[0];
              if (preferredShippingMethod) {
                thisShippingMethod = preferredShippingMethod;
              }
            }
          }
        }
        if (!thisShippingAddress || !thisShippingMethod) {
          throw new Error(
            "Unable to set the shipping address on the cart. The cart does not have a shipping address or a shipping method."
          );
        }
        ((window as any).checkoutConfig as any).selectedShippingMethod =
          thisShippingAddress;

        return [thisShippingAddress, thisShippingMethod];
      }, [
        cartId,
        dataSource_checkoutRegions_data,
        customerInformationData,
        dataMutation_setShippingAddressesOnCart_mutateAsync,
      ]);
    const handle_shippingAddress__setTheShippingMethod = React.useCallback(
      async (thisShippingMethod: AvailableShippingMethod) => {
        if (!cartId) {
          throw new Error(
            "Cannot set the shipping method on the cart because the cart ID is not available."
          );
        }

        const thisShippingMethod_transformedIntoShippingMethodInput: ShippingMethodInput | null =
          transform_availableShippingMethod_toShippingMethodInput(
            thisShippingMethod
          );
        if (!thisShippingMethod_transformedIntoShippingMethodInput) {
          throw new Error(
            "Cannot set the shipping method on the cart because the shipping method is not available."
          );
        }
        const request: DataMutationShippingSetShippingMethodsOnCart_Request = {
          cart_id: cartId,
          shipping_methods: [
            thisShippingMethod_transformedIntoShippingMethodInput,
          ],
        };
        const dataMutation_setShippingMethodsOnCart_response =
          await dataMutation_setShippingMethodsOnCart_mutateAsync(request);
        assertCartItemsAvailable(dataMutation_setShippingMethodsOnCart_response);

        // Triple Whale shippingSubmitted, keyed by the lines and the selected method.
        analytics.shippingSubmitted({
          methodCode: `${thisShippingMethod_transformedIntoShippingMethodInput.carrier_code}_${thisShippingMethod_transformedIntoShippingMethodInput.method_code}`,
          email: isCustomerLoggedIn
            ? customerInformationData?.email || null
            : getDeliveryEmailValue(),
        });
      },
      [
        cartId,
        dataMutation_setShippingMethodsOnCart_mutateAsync,
        analytics,
        isCustomerLoggedIn,
        customerInformationData?.email,
        getDeliveryEmailValue,
      ]
    );
    const handle_shippingAddress_authoriseDeliveryToBeLeftWithoutSignature =
      React.useCallback(async () => {
        const deliveryDetailsFormState = getDeliveryDetailsFormState();
        const shippingAddressFormState = deliveryDetailsFormState?.shipping;

        const hasTheUserRequestedAuthorization =
          shippingAddressFormState?.authoriseDeliveryToBeLeftWithoutSignature
            ?.value || false;
        if (!hasTheUserRequestedAuthorization) {
          return;
        }

        if (!deliveryDetailsFormState) {
          throw new Error(
            "Cannot authorize the delivery to be left without a signature on the cart because the delivery details form state is not available."
          );
        }
        const address: LegacyMagento_ShippingInformation_CartAddressInput | null =
          transform_shippingForm_toLegacyMagento_ShippingInformation_CartAddressInput(
            dataSource_checkoutRegions_data,
            customerInformationData,
            deliveryDetailsFormState
          );
        if (!address) {
          throw new Error(
            "Cannot authorize the delivery to be left without a signature on the cart because the address is not available."
          );
        }
        if (isCustomerLoggedIn) {
          const request: DataMutationShippingMemberSetAuthorityToLeave_Request =
            {
              address,
            };
          const dataMutation_member_setAuthorityToLeave_response =
            await dataMutation_member_setAuthorityToLeave_mutateAsync(request);
        } else {
          if (!cartId) {
            throw new Error(
              "Cannot authorize the delivery to be left without a signature on the cart because the cart ID is not available."
            );
          }
          const request: DataMutationShippingSetAuthorityToLeave_Request = {
            cartId,
            address,
          };
          const dataMutation_setAuthorityToLeave_response =
            await dataMutation_setAuthorityToLeave_mutateAsync(request);
        }
      }, [
        isCustomerLoggedIn,
        cartId,
        dataSource_checkoutRegions_data,
        customerInformationData,
        dataMutation_setAuthorityToLeave_mutateAsync,
        dataMutation_member_setAuthorityToLeave_mutateAsync,
      ]);
    const handle_shippingAddress = React.useCallback(async () => {
      await handle_shippingAddress__saveTheAddress();
      const [thisShippingAddress, thisShippingMethod] =
        await handle_shippingAddress__setTheAddress();
      await handle_shippingAddress__setTheShippingMethod(thisShippingMethod);

      await handle_shippingAddress_authoriseDeliveryToBeLeftWithoutSignature();

      return thisShippingAddress;
    }, [
      handle_shippingAddress__saveTheAddress,
      handle_shippingAddress__setTheAddress,
      handle_shippingAddress__setTheShippingMethod,
      handle_shippingAddress_authoriseDeliveryToBeLeftWithoutSignature,
    ]);

    const handle_billingAddress__saveTheAddress =
      React.useCallback(async () => {
        const doesTheAddressNeedToBeSaved =
          doesTheAddressNeedToBeSaved_billing();
        if (doesTheAddressNeedToBeSaved) {
          const deliveryDetailsFormState = getDeliveryDetailsFormState();
          const billingAddressFormState = deliveryDetailsFormState?.billing;
          if (billingAddressFormState) {
            await handle_generic_saveTheAddress(billingAddressFormState);
          }
        }
      }, [doesTheAddressNeedToBeSaved_billing, handle_generic_saveTheAddress]);
    const handle_billingAddress__setTheAddress = React.useCallback(async () => {
      if (!cartId) {
        throw new Error(
          "Cannot set the billing address on the cart because the cart ID is not available."
        );
      }

      const deliveryDetailsFormState = getDeliveryDetailsFormState();
      if (!deliveryDetailsFormState) {
        throw new Error(
          "Cannot set the billing address on the cart because the delivery details form state is not available."
        );
      }

      const addressInput = presentationMyBagOrder?.isThisAVirtualOrder
        ? deliveryDetailsFormState.billing
        : (deliveryDetailsFormState.isBillingAndShippingTheSame?.value
          ? deliveryDetailsFormState.shipping
          : deliveryDetailsFormState.billing)

      const address: CartAddressInput | null =
        transform_genericAddressForm_toCartAddressInput(
          dataSource_checkoutRegions_data,
          customerInformationData,
          addressInput
        );
      if (!address) {
        throw new Error(
          "Cannot set the billing address on the cart because the address is invalid without a Country."
        );
      }
      const billing_address: BillingAddressInput = {
        address,
      };
      const request: DataMutationShippingSetBillingAddressesOnCart_Request = {
        cart_id: cartId,
        billing_address,
      };
      const dataMutation_setBillingAddressesOnCart_response =
        await dataMutation_setBillingAddressesOnCart_mutateAsync(request);
      assertCartItemsAvailable(dataMutation_setBillingAddressesOnCart_response);

      return dataMutation_setBillingAddressesOnCart_response.billing_address;
    }, [
      cartId,
      dataSource_checkoutRegions_data,
      customerInformationData,
      dataMutation_setBillingAddressesOnCart_mutateAsync,
      presentationMyBagOrder?.isThisAVirtualOrder,
    ]);
    const handle_billingAddress = React.useCallback(
      async (
        shippingAddressThatWasRegisteredOnTheBackend: ShippingCartAddress | null
      ) => {
        await handle_billingAddress__saveTheAddress();

        return await handle_billingAddress__setTheAddress();
      },
      [
        handle_billingAddress__saveTheAddress,
        handle_billingAddress__setTheAddress,
      ]
    );

    const handle_magentoLegacy_frontendQuote_updateWithRegisteredBackendQuote =
      React.useCallback(
        async (
          shippingAddressThatWasRegisteredOnTheBackend: ShippingCartAddress | null,
          billingAddressThatWasRegisteredOnTheBackend: BillingCartAddress
        ) => {
          return new Promise((resolve, reject) => {
            try {
              (window as any).getCheckoutObjects(
                (
                  addressConverter: any,
                  quote: any,
                  customerData: any,
                  checkoutData: any
                ) => {
                  const emailFromForm = getDeliveryEmailValue();

                  quote.guestEmail = emailFromForm;

                  const billingAddress_readyForLegacyMagentoStackConversion: any =
                    {
                      ...billingAddressThatWasRegisteredOnTheBackend,
                    };

                  const { code, label } =
                    billingAddressThatWasRegisteredOnTheBackend.region;
                  const billingRegion =
                    (dataSource_checkoutRegions_data?.available_regions || []).find(
                      (region) => region.code === code
                    );

                  billingAddress_readyForLegacyMagentoStackConversion.region =
                    label;
                  billingAddress_readyForLegacyMagentoStackConversion.region_code =
                    code;
                  billingAddress_readyForLegacyMagentoStackConversion.region_id =
                    billingRegion ? billingRegion.id : 0;
                  billingAddress_readyForLegacyMagentoStackConversion.custom_attributes =
                    {
                      "confirm-customer-email": emailFromForm,
                    };

                  quote.shippingAddress(
                    addressConverter.formAddressDataToQuoteAddress(
                      shippingAddressThatWasRegisteredOnTheBackend
                        ? shippingAddressThatWasRegisteredOnTheBackend
                        : billingAddressThatWasRegisteredOnTheBackend
                    )
                  );
                  quote.billingAddress(
                    addressConverter.formAddressDataToQuoteAddress(
                      billingAddressThatWasRegisteredOnTheBackend
                    )
                  );

                  resolve({
                    done: true,
                  });
                }
              );
            } catch (anyError) {
              reject(anyError);
            }
          });
        },
        [
          dataSource_checkoutRegions_data,
          getDeliveryEmailValue,
        ]
      );
    const handle_navigateToThePaymentPage = React.useCallback(() => {
      navigateTo(ReactCheckout2022Route.payment);
    }, [navigateTo]);
    const handle_navigateToTheDeliveryPage = React.useCallback(() => {
      navigateTo(ReactCheckout2022Route.delivery_details);
    }, [navigateTo]);

    /**
     * Sign: the actual kickoff internals
     */
    const kickoffInternals = React.useCallback(async () => {
      await handle_cart_guestCart_setTheEmail();

      const shippingAddressThatWasRegisteredOnTheBackend =
        presentationMyBagOrder?.isThisAVirtualOrder
          ? null
          : await handle_shippingAddress();

      const billingAddressThatWasRegisteredOnTheBackend =
        await handle_billingAddress(
          shippingAddressThatWasRegisteredOnTheBackend
        );

      if (!billingAddressThatWasRegisteredOnTheBackend) {
        throw new Error(
          "The billing address was not registered on the backend."
        );
      }

      await handle_magentoLegacy_frontendQuote_updateWithRegisteredBackendQuote(
        presentationMyBagOrder?.isThisAVirtualOrder
          ? null
          : shippingAddressThatWasRegisteredOnTheBackend,
        billingAddressThatWasRegisteredOnTheBackend
      );
    }, [
      handle_cart_guestCart_setTheEmail,
      handle_shippingAddress,
      handle_billingAddress,
      handle_magentoLegacy_frontendQuote_updateWithRegisteredBackendQuote,
      presentationMyBagOrder?.isThisAVirtualOrder,
    ]);

    /**
     * Sign: public interface
     */
    const kickoffThePaymentPage: DeliveryDetailsPaymentPageKickoff_ControllerInterface["kickoffThePaymentPage"] =
      React.useCallback(() => {
        const deps: KickoffDeps = {
          setIsLoading,
          enablePooling: dataSourceMyBagTotals_enablePoolingInvalidations,
          flushPooling: dataSourceMyBagTotals_flushPooledInvalidations,
          snapshotForm: () => {
            deliveryDetailsFormStateSnapshotRef.current =
              getDeliveryDetailsFormState();
          },
          getLegacyCheckoutConfig: readLegacyCheckoutConfig,
          getLastKnownAddress,
          updateLegacyCheckoutConfig: handle_magentoLegacy_checkoutConfig_updates,
          navigateToPayment: handle_navigateToThePaymentPage,
          setHasBeenToPaymentPage: setHasBeenToThePaymentPage,
          kickoffInternals,
          setProminentError,
          navigateToDelivery: handle_navigateToTheDeliveryPage,
          clearSnapshot: () => {
            deliveryDetailsFormStateSnapshotRef.current = null;
          },
        };
        return runPaymentPageKickoff(deps);
      }, [
        setIsLoading,
        setProminentError,
        handle_magentoLegacy_checkoutConfig_updates,
        kickoffInternals,
        handle_navigateToThePaymentPage,
        handle_navigateToTheDeliveryPage,
        setHasBeenToThePaymentPage,
        getDeliveryDetailsFormState,
        dataSourceMyBagTotals_enablePoolingInvalidations,
        dataSourceMyBagTotals_flushPooledInvalidations,
      ]);

    const value: DeliveryDetailsPaymentPageKickoffConsumerInterface =
      React.useMemo(
        () => ({
          isLoading,
          hasBeenToThePaymentPage,
        }),
        [isLoading, hasBeenToThePaymentPage]
      );
    const controllerValue: DeliveryDetailsPaymentPageKickoff_ControllerInterface =
      React.useMemo(
        () => ({
          kickoffThePaymentPage,
          primeDeliveryDetailsSnapshot,
        }),
        [kickoffThePaymentPage, primeDeliveryDetailsSnapshot]
      );
    return (
      <DeliveryDetailsPaymentPageKickoffContext.Provider value={value}>
        <DeliveryDetailsPaymentPageKickoff_ControllerContext.Provider
          value={controllerValue}
        >
          {props.children || []}
        </DeliveryDetailsPaymentPageKickoff_ControllerContext.Provider>
      </DeliveryDetailsPaymentPageKickoffContext.Provider>
    );
  };

export const useDeliveryDetailsPaymentPageKickoff = () => {
  const context = React.useContext(DeliveryDetailsPaymentPageKickoffContext);
  if (context === defaultValue) {
    throw new Error(
      "useDeliveryDetailsPaymentPageKickoff must be used within a DeliveryDetailsPaymentPageKickoffProvider"
    );
  }
  return context;
};

export const useDeliveryDetailsPaymentPageKickoff_asController = () => {
  const context = React.useContext(
    DeliveryDetailsPaymentPageKickoff_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDeliveryDetailsPaymentPageKickoff_asController must be used within a DeliveryDetailsPaymentPageKickoffProvider"
    );
  }
  return context;
};

const transform_shippingForm_toCustomerAddressInput = (
  dataSource_checkoutRegions_data: CheckoutRegions_Response | undefined,
  genericAddressFormState: GenericAddressFormExpects
) => {
  const thisRegion_sourceOfTruth = (
    dataSource_checkoutRegions_data?.available_regions || []
  ).find(
    (availableRegion) =>
      availableRegion.code === genericAddressFormState.addressManual.state.value
  );
  const region: NonNullable<CustomerAddressInput["region"]> = {
    region: thisRegion_sourceOfTruth?.name || genericAddressFormState.addressManual.state.value || "",
    region_code: thisRegion_sourceOfTruth?.code || genericAddressFormState.addressManual.state.value || "",
    region_id: thisRegion_sourceOfTruth?.id || 0,
  };

  const areThereAnyStreetInputs =
    !!genericAddressFormState.addressManual.addressLine1.value ||
    !!genericAddressFormState.addressManual.addressLine2.value;
  const street = areThereAnyStreetInputs
    ? [
        genericAddressFormState.addressManual.addressLine1.value,
        genericAddressFormState.addressManual.addressLine2.value,
      ].reduce((acc, value) => {
        if (value && value.length > 0) {
          return [...acc, ...[value]];
        }
        return acc;
      }, [] as string[])
    : undefined;
  const addressInput: CustomerAddressInput = {
    firstname: genericAddressFormState.firstName.value,
    lastname: genericAddressFormState.lastName.value,
    street,
    city: genericAddressFormState.addressManual.suburb.value,
    postcode: genericAddressFormState.addressManual.postcode.value,
    country_code: (
      (genericAddressFormState.addressManual.country.value ||
        ACountry.Australia) as `${ACountry}`
    ).toUpperCase() as CustomerAddressInput["country_code"],
    telephone: genericAddressFormState.mobile.value,
    region,
    default_shipping: false,
    default_billing: false,
  };
  return addressInput;
};

const transform_genericAddressForm_toCartAddressInput = (
  dataSource_checkoutRegions_data: CheckoutRegions_Response | undefined,
  customerInformationData: CustomerInformation_Response | undefined,
  genericAddressFormState:
    | ShippingAddressFormExpects
    | BillingAddressFormExpects
): CartAddressInput | null => {
  let region: string | undefined = undefined;
  let region_id: any = 0;
  let country_code: CartAddressInput["country_code"] | undefined = undefined;
  const thisSavedAddressId = (
    genericAddressFormState as Partial<ShippingAddressFormExpects>
  ).savedAddress?.value;

  const correspondingSavedAddress = customerInformationData?.addresses?.find(
    (address) => address.id === thisSavedAddressId
  );
  const canIUseMySavedAddressISelected =
    customerInformationData &&
    correspondingSavedAddress &&
    !!(genericAddressFormState as Partial<ShippingAddressFormExpects>)
      .savedAddress?.enabled;
  const thisSavedAddress = canIUseMySavedAddressISelected
    ? correspondingSavedAddress
    : undefined;
  if (thisSavedAddress) {
    if (thisSavedAddress.region && thisSavedAddress.region.region_id) {
      region_id = thisSavedAddress.region.region_id;
    } else {
      const regionCodeToSearch = thisSavedAddress.region?.region_code;
      const thisRegion_sourceOfTruth = (
        dataSource_checkoutRegions_data?.available_regions || []
      ).find((availableRegion) => availableRegion.code === regionCodeToSearch);
      region_id = thisRegion_sourceOfTruth?.id || 0;
    }

    region =
      thisSavedAddress.region
        ? thisSavedAddress.region.region_code || ""
        : "";
    country_code = thisSavedAddress.country_code;
  } else {
    const regionCodeToSearch =
      genericAddressFormState.addressManual.state.value;
    const thisRegion_sourceOfTruth = (
      dataSource_checkoutRegions_data?.available_regions || []
    ).find((availableRegion) => availableRegion.code === regionCodeToSearch);
    region = genericAddressFormState.addressManual.state.value || "";
    region_id = thisRegion_sourceOfTruth?.id || 0;
    country_code =
      genericAddressFormState.addressManual.country.value?.toUpperCase();
  }

  if (!country_code) {
    return null;
  }

  let streetsRaw: (string | undefined | null)[] = [];
  if (thisSavedAddress) {
    streetsRaw = [...streetsRaw, ...(thisSavedAddress.street || [])];
  } else {
    streetsRaw = [
      ...streetsRaw,
      ...[
        genericAddressFormState.addressManual.addressLine1.value,
        genericAddressFormState.addressManual.addressLine2.value,
      ],
    ];
  }
  const street: string[] = streetsRaw.reduce((acc, value) => {
    if (value && value.length > 0) {
      return [...acc, ...[value]];
    }
    return acc;
  }, [] as string[]);

  const cartAddressInput: CartAddressInput = {
    firstname: thisSavedAddress?.firstname
      ? thisSavedAddress?.firstname
      : genericAddressFormState?.firstName?.value,
    lastname: thisSavedAddress?.lastname
      ? thisSavedAddress.lastname
      : genericAddressFormState?.lastName?.value,
    street,
    city: thisSavedAddress?.city
      ? thisSavedAddress.city
      : genericAddressFormState?.addressManual?.suburb?.value,
    postcode: thisSavedAddress?.postcode
      ? thisSavedAddress.postcode
      : genericAddressFormState?.addressManual?.postcode?.value,
    telephone: thisSavedAddress?.telephone
      ? thisSavedAddress.telephone
      : genericAddressFormState?.mobile?.value,
    region,
    region_id,
    country_code,
  };
  return cartAddressInput;
};

const transform_shippingForm_toLegacyMagento_ShippingInformation_CartAddressInput =
  (
    dataSource_checkoutRegions_data: CheckoutRegions_Response | undefined,
    customerInformationData: CustomerInformation_Response | undefined,
    deliveryDetailsFormState: DeliveryDetailsForm
  ): LegacyMagento_ShippingInformation_CartAddressInput | null => {
    const email = deliveryDetailsFormState.shippingEmailConfirmation.value;
    if (!email) {
      return null;
    }
    const regularCartAddressInput =
      transform_genericAddressForm_toCartAddressInput(
        dataSource_checkoutRegions_data,
        customerInformationData,
        deliveryDetailsFormState.shipping
      );
    if (!regularCartAddressInput) {
      return null;
    }

    const value: LegacyMagento_ShippingInformation_CartAddressInput = {
      email,

      country_id:
        regularCartAddressInput.country_code as MagentoCountryCodeEnum,

      firstname: regularCartAddressInput.firstname,
      lastname: regularCartAddressInput.lastname,
      street: regularCartAddressInput.street,
      city: regularCartAddressInput.city,
      region: regularCartAddressInput.region,
      region_id: regularCartAddressInput.region_id,
      postcode: regularCartAddressInput.postcode,
      telephone: regularCartAddressInput.telephone,
    };
    return value;
  };

const transform_availableShippingMethod_toShippingMethodInput = (
  shippingMethod: AvailableShippingMethod
): ShippingMethodInput | null => {
  if (!shippingMethod.carrier_code || !shippingMethod.method_code) {
    return null;
  }
  const value: ShippingMethodInput = {
    carrier_code: shippingMethod.carrier_code,
    method_code: shippingMethod.method_code,
  };
  return value;
};
